/**
 * משימות — an entity list with inline completion.
 *
 * The "שלי" filter is first because it is the bookkeeper's home screen. Tasks the system
 * created for itself are marked, so a reader can tell an escalation from something a
 * colleague typed — that distinction is what stops automatic tasks feeling like noise.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@onecpa/shared";
import {
  Badge,
  Button,
  Checkbox,
  type Column,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  StatusBadge,
  TabStrip,
  Textarea,
} from "@onecpa/ui";
import { api, type TaskRow } from "../api/client.js";
import { useSession } from "../app/session.js";

type Filter = "mine" | "open" | "done" | "all";

const PRIORITY_LABELS: Record<string, string> = {
  low: "נמוכה",
  normal: "רגילה",
  high: "גבוהה",
  urgent: "דחוף",
};

export function Tasks() {
  const session = useSession();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>("mine");
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["tasks", session.orgId, filter],
    queryFn: () =>
      api.tasks.list(session.orgId, {
        ...(filter === "mine" ? { mine: true, status: "open" } : {}),
        ...(filter === "open" ? { status: "open" } : {}),
        ...(filter === "done" ? { status: "done" } : {}),
      }),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      api.tasks.update(session.orgId, input.id, input.body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const rows = data?.tasks ?? [];

  const columns: Column<TaskRow>[] = [
    {
      key: "done",
      header: "",
      width: "44px",
      cell: (row) => (
        <Checkbox
          label=""
          aria-label={`סימון "${row.title}" כבוצעה`}
          checked={row.status === "done"}
          onChange={(event) =>
            update.mutate({ id: row.id, body: { status: event.target.checked ? "done" : "open" } })
          }
        />
      ),
    },
    {
      key: "title",
      header: "משימה",
      sticky: true,
      width: "300px",
      cell: (row) => (
        <span className="min-w-0">
          <span
            className={`block truncate text-sm ${
              row.status === "done" ? "text-ink-faint line-through" : "font-semibold text-ink"
            }`}
          >
            {row.title}
          </span>
          {row.description && (
            <span className="block truncate text-2xs text-ink-faint">{row.description}</span>
          )}
        </span>
      ),
    },
    {
      key: "client",
      header: "לקוח",
      cell: (row) =>
        row.clientId ? (
          <Link
            to="/clients/$clientId"
            params={{ clientId: row.clientId }}
            className="link-underline text-xs text-ink"
          >
            {row.clientName}
          </Link>
        ) : (
          <span className="text-xs text-ink-faint">כללי</span>
        ),
    },
    {
      key: "assignee",
      header: "אחראי",
      hideOnSmall: true,
      cell: (row) => (
        <span className="text-xs text-ink-muted">{row.assignedUserName ?? "—"}</span>
      ),
    },
    {
      key: "due",
      header: "מועד",
      cell: (row) =>
        row.dueDate ? (
          <bdi
            className={`tnum text-xs ${
              row.status === "open" && row.dueDate < new Date().toISOString().slice(0, 10)
                ? "font-semibold text-ochre"
                : "text-ink-muted"
            }`}
          >
            {formatDate(row.dueDate)}
          </bdi>
        ) : (
          "—"
        ),
    },
    {
      key: "meta",
      header: "",
      align: "end",
      cell: (row) => (
        <span className="flex items-center justify-end gap-1.5">
          {row.recurrence && <Badge tone="neutral">חוזרת</Badge>}
          {/* System-created tasks are marked so an escalation is distinguishable from
              something a colleague typed. */}
          {row.sourceTable && <Badge tone="info">אוטומטית</Badge>}
          {row.priority === "urgent" && <Badge tone="danger">דחוף</Badge>}
          {row.priority === "high" && <Badge tone="warning">גבוהה</Badge>}
          <StatusBadge vocabulary="task" status={row.status} />
        </span>
      ),
    },
  ];

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  return (
    <>
      <PageHeader
        title="משימות"
        subtitle={`${rows.length} משימות`}
        action={
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            משימה חדשה
          </Button>
        }
      >
        <TabStrip
          tabs={[
            { id: "mine", label: "שלי" },
            { id: "open", label: "פתוחות" },
            { id: "done", label: "בוצעו" },
            { id: "all", label: "הכל" },
          ]}
          active={filter}
          onSelect={(id) => setFilter(id as Filter)}
        />
      </PageHeader>

      <div className="p-5">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          caption="משימות המשרד"
          empty={
            <EmptyState
              title={filter === "mine" ? "אין משימות פתוחות עבורך" : "אין משימות"}
              description="משימות נוצרות גם אוטומטית — כשתיק מוכן לעבודה או כשחוב חורג מ-90 יום."
              icon="check"
              action={
                <Button variant="primary" onClick={() => setCreating(true)}>
                  משימה חדשה
                </Button>
              }
            />
          }
        />
      </div>

      <CreateTaskDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function CreateTaskDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [priority, setPriority] = useState("normal");
  const [dueDate, setDueDate] = useState("");
  const [recurrence, setRecurrence] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: clients } = useQuery({
    queryKey: ["clients", session.orgId, "", "active"],
    queryFn: () => api.clients.list(session.orgId, { status: "active", limit: 200 }),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api.tasks.create(session.orgId, {
        title: title.trim(),
        priority,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(clientId ? { clientId } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(recurrence ? { recurrence } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      session.toast("המשימה נוספה");
      setTitle("");
      setDescription("");
      onClose();
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="משימה חדשה"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant="primary"
            disabled={title.trim().length === 0}
            loading={create.isPending}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            הוספה
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="כותרת" required>
          {({ id }) => (
            <Input id={id} value={title} onChange={(event) => setTitle(event.target.value)} />
          )}
        </Field>
        <Field label="פירוט">
          {({ id }) => (
            <Textarea
              id={id}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
        <Field label="לקוח" help="ניתן להשאיר ריק למשימה כללית">
          {({ id }) => (
            <Select id={id} value={clientId} onChange={(event) => setClientId(event.target.value)}>
              <option value="">כללי</option>
              {(clients?.clients ?? []).map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="עדיפות">
            {({ id }) => (
              <Select
                id={id}
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="מועד">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label="חזרתיות" help="משימה חוזרת נוצרת מחדש אוטומטית לאחר סיומה">
          {({ id }) => (
            <Select
              id={id}
              value={recurrence}
              onChange={(event) => setRecurrence(event.target.value)}
            >
              <option value="">חד-פעמית</option>
              <option value="monthly">חודשית</option>
              <option value="quarterly">רבעונית</option>
              <option value="annual">שנתית</option>
            </Select>
          )}
        </Field>
        {error && (
          <p role="alert" className="text-xs font-semibold text-red-ink">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
